import React, { useEffect, useMemo, useState } from 'react';
import BunnyLoader from '../ui/BunnyLoader';
import { hirelaneApi } from '../../services/api';
import { interviewAttendeesSummary } from '../../utils/hirelaneInterviewAttendees';
import { hirelanePipelineStageLabel } from '../../utils/hirelaneLabels';

function mergeTimeline(historyRows, interviews) {
  const events = [];

  for (const h of historyRows || []) {
    events.push({
      sortKey: new Date(h.created_at || 0).getTime(),
      title:
        h.action_type === 'schedule_interview'
          ? 'Interview scheduled'
          : h.action_type === 'interview_outcome'
            ? `Outcome: ${h.new_status}`
            : h.action_type === 'reopen'
              ? 'Candidate reopened'
              : `${hirelanePipelineStageLabel(h.previous_status)} → ${hirelanePipelineStageLabel(h.new_status)}`,
      detail: h.comments || '',
      meta: h.reason ? `Reason: ${h.reason}` : null,
      sub: h.recommended_next_step ? `Next step: ${h.recommended_next_step}` : null
    });
  }

  for (const inv of interviews || []) {
    events.push({
      sortKey: new Date(inv.scheduled_at || inv.created_at || 0).getTime(),
      title:
        inv.status === 'Completed'
          ? `${inv.display_round_label} completed`
          : `${inv.display_round_label} (${inv.status})`,
      detail: inv.notes || '',
      meta: interviewAttendeesSummary(inv) ? `Interviewers: ${interviewAttendeesSummary(inv)}` : null,
      sub:
        inv.status === 'Completed' && inv.outcome && inv.outcome !== 'Pending'
          ? `Outcome: ${inv.outcome}`
          : null
    });
  }

  events.sort((a, b) => a.sortKey - b.sortKey);
  return events;
}

export default function CandidateInterviewTimeline({ candidateId, refreshKey = 0, style, className = '' }) {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [historyRows, setHistoryRows] = useState([]);
  const [interviews, setInterviews] = useState([]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        setLoading(true);
        setError(null);
        const [hist, inv] = await Promise.all([
          hirelaneApi.listCandidateStatusHistory(candidateId),
          hirelaneApi.listCandidateInterviews(candidateId)
        ]);
        if (!cancelled) {
          setHistoryRows(Array.isArray(hist) ? hist : []);
          setInterviews(Array.isArray(inv) ? inv : []);
        }
      } catch (e) {
        if (!cancelled) setError(e?.message || 'Failed to load timeline');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [candidateId, refreshKey]);

  const events = useMemo(() => mergeTimeline(historyRows, interviews), [historyRows, interviews]);

  const latestTitle =
    !loading && !error && events.length > 0 ? events[events.length - 1].title : null;

  const summary = (
    <summary className="hirelane-detail__timelineSummary">
      <span className="hirelane-detail__timelineSummaryChevron" aria-hidden>
        ›
      </span>
      <span className="hirelane-detail__timelineSummaryCol">
        <span className="hirelane-detail__timelineSummaryTop">
          <span className="hirelane-detail__timelineSummaryLabel">Pipeline & interviews</span>
          {latestTitle ? (
            <span className="hirelane-detail__timelineSummaryPreview" title={latestTitle}>
              <span className="hirelane-detail__timelineSummaryDot" aria-hidden>
                {' '}
                ·{' '}
              </span>
              {latestTitle}
            </span>
          ) : null}
        </span>
        <span className="hirelane-detail__timelineSummarySub">Stages and interview rounds</span>
      </span>
    </summary>
  );

  const wrapClass = ['hirelane-detail__timelineDisclosure', className].filter(Boolean).join(' ');

  if (loading) {
    return (
      <details className={wrapClass} style={style}>
        {summary}
        <div className="hirelane-detail__timelinePanel">
          <BunnyLoader />
        </div>
      </details>
    );
  }
  if (error) {
    return (
      <details className={wrapClass} style={style}>
        {summary}
        <div className="hirelane-detail__timelinePanel hirelane-detail__timelinePanel--error">{error}</div>
      </details>
    );
  }

  if (events.length === 0) {
    return (
      <details className={wrapClass} style={style}>
        {summary}
        <div className="hirelane-detail__timelinePanel">No timeline events yet.</div>
      </details>
    );
  }

  return (
    <details className={wrapClass} style={style}>
      {summary}
      <div className="hirelane-detail__timeline">
        <ul className="hirelane-detail__timelineList">
          {events.map((ev, i) => (
            <li key={`${ev.sortKey}-${i}`} className="hirelane-detail__timelineEvent">
              <div className="hirelane-detail__timelineEventTitle">{ev.title}</div>
              {ev.detail ? <div className="hirelane-detail__timelineEventDetail">{ev.detail}</div> : null}
              {ev.meta ? <div className="hirelane-detail__timelineEventMeta">{ev.meta}</div> : null}
              {ev.sub ? <div className="hirelane-detail__timelineEventSub">{ev.sub}</div> : null}
            </li>
          ))}
        </ul>
      </div>
    </details>
  );
}
